import { useCallback, useMemo, useState } from "react"
import { Box, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import EditIcon from "@mui/icons-material/Edit"

import type { Device, InstallationDetailsForm } from "types/device.types"

import { useDeviceQuery, useUpdateDeviceSettingsMutation } from "features/api"
import { buildGetErrorMessage, snackbarMutation } from "helpers/utils/mutations"
import CardWithTitle from "widgets/common/CardWithTitle"
import useSpan from "helpers/hooks/spans/useSpan"
import useTower from "helpers/hooks/towers/useTower"
import usePowerline from "helpers/hooks/powerlines/usePowerline"
import usePermissions from "helpers/hooks/usePermissions"
import InstallationDetailsView from "./InstallationDetailsView"
import {
  getDefaultValues,
  getTowerBId,
  isDeviceRefetchPending,
  mapFormToSettings,
  EMPTY_FORM_STATE,
} from "./helpers"
import EditInstallationDetails from "./EditInstallationDetails"

interface InstallationDetailsProps {
  device: Device
}

const InstallationDetails = ({ device }: InstallationDetailsProps) => {
  const { t, i18n } = useTranslation()
  const { isBackoffice } = usePermissions()
  const [isEditing, setIsEditing] = useState(false)
  const [
    updateDeviceSettings,
    { isLoading: isSubmitting, fulfilledTimeStamp: updatedAt },
  ] = useUpdateDeviceSettingsMutation()
  // Timestamps, not references: a refetch returning identical data keeps the same object
  const { fetchedAt } = useDeviceQuery(
    { id: device.less_id },
    { selectFromResult: ({ fulfilledTimeStamp }) => ({ fetchedAt: fulfilledTimeStamp }) },
  )
  const isAwaitingRefetch = isDeviceRefetchPending(updatedAt, fetchedAt)

  const deviceSettings = device.settings
  const installationType = deviceSettings?.installation_type

  const { powerline } = usePowerline({
    id: deviceSettings?.powerline_id,
  })
  const { span } = useSpan({
    id: installationType === "powerline" ? deviceSettings?.span_id : undefined,
  })
  const { tower: towerA, isLoading: towerALoading } = useTower({
    id: deviceSettings?.tower_id,
  })
  const towerBId = useMemo(
    () => getTowerBId(deviceSettings?.tower_id, span?.tower_ids),
    [deviceSettings?.tower_id, span?.tower_ids],
  )
  const { tower: towerB, isLoading: towerBLoading } = useTower({ id: towerBId })
  const isLoading = useMemo(
    () => towerALoading || (installationType === "powerline" && towerBLoading),
    [installationType, towerALoading, towerBLoading],
  )

  const defaultValues = useMemo(
    () =>
      deviceSettings
        ? getDefaultValues(
            deviceSettings,
            powerline,
            span,
            towerA,
            towerB,
            t,
            i18n.resolvedLanguage || "en",
          )
        : null,
    [deviceSettings, i18n.resolvedLanguage, powerline, span, t, towerA, towerB],
  )

  const handleOpen = useCallback(() => {
    if (isBackoffice) {
      setIsEditing(true)
    }
  }, [isBackoffice])

  const handleClose = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleSubmit = useCallback(
    async (data: InstallationDetailsForm) => {
      const settings = mapFormToSettings(data, i18n.resolvedLanguage || "en")
      return snackbarMutation({
        mutation: updateDeviceSettings({ less_id: device.less_id, settings }).unwrap(),
        getErrorMessage: buildGetErrorMessage(
          t("error.UPDATING_ITEM", { item: t("device_settings.INSTALLATION_DETAILS") }),
        ),
        getSuccessMessage: () =>
          t("success.UPDATING_ITEM", {
            item: t("device_settings.INSTALLATION_DETAILS"),
            count: 2,
            context: "male",
          }),
      })
        .then(() => {
          setIsEditing(false)
        })
        .catch(() => {})
    },
    [device.less_id, i18n.resolvedLanguage, t, updateDeviceSettings],
  )

  return (
    <CardWithTitle
      titleKey={"device_settings.INSTALLATION_DETAILS"}
      actionPermission={isBackoffice}
      isDoingAction={isEditing}
      handleAction={handleOpen}
      actionIcon={<EditIcon fontSize="small" />}
    >
      {isEditing ? (
        <EditInstallationDetails
          defaultValues={defaultValues ?? EMPTY_FORM_STATE}
          onSubmit={handleSubmit}
          onClose={handleClose}
          installationType={installationType ?? "uninstalled"}
          deviceName={device.name}
          isLoading={isSubmitting}
          powerline={powerline}
          span={span}
          towerA={towerA}
          towerB={towerB}
        />
      ) : (isAwaitingRefetch && installationType && defaultValues) ||
        (installationType && installationType !== "uninstalled" && defaultValues) ? (
        <InstallationDetailsView
          installationType={installationType}
          values={defaultValues}
          isLoading={isLoading || isAwaitingRefetch}
        />
      ) : (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography color="text.secondary">
            {t("device_installation.DEVICE_NOT_INSTALLED")}
          </Typography>
        </Box>
      )}
    </CardWithTitle>
  )
}

export default InstallationDetails
